import descriptor from "./descriptors/com.carrotsearch.lingo3g.Lingo3GClusteringAlgorithm.json";
import { persistentStore } from "@carrotsearch/ui/store/persistent-store.js";

import _set from "lodash.set";

import {
  advanced,
  collectDefaults,
  createExcludedLabelsSetting,
  getDescriptorsById,
  settingFromDescriptor
} from "@carrot2/app/service/algorithms/attributes.js";
import { createLanguageSetting } from "@carrot2/app/service/algorithms/language.js";
import { storeAccessors } from "@carrotsearch/ui/settings/Setting.js";

const descriptorsById = getDescriptorsById(descriptor);

const settingFrom = (id, overrides) =>
  settingFromDescriptor(descriptorsById, id, overrides);
const clusterSettings = [
  settingFrom("clusters.minClusterSize"), // TODO: combine into a single min-max control
  settingFrom("clusters.maxClusterSize"),
  settingFrom("clusters.preciseDocumentAssignment"),
  advanced(settingFrom("clusters.preciseDocumentAssignmentSlopMultiplier")),
  advanced(settingFrom("clusters.preciseDocumentAssignmentSlopOffset")),
  advanced(settingFrom("clusters.allowOneDocumentClusters")),
  advanced(settingFrom("clusters.combinedClusterScoreBalance")),
  advanced(settingFrom("clusters.normalizeScores"))
];
const hierarchySettings = [
  settingFrom("hierarchy.maxHierarchyDepth"),
  settingFrom("hierarchy.clusterCountBase"),
  settingFrom("hierarchy.minClusterSizeForSubclusters"),
  advanced(settingFrom("hierarchy.documentCoverageTarget")),
  advanced(settingFrom("hierarchy.maxClusteringPassesSub")),
  advanced(settingFrom("hierarchy.maxClusteringPassesTop")),
  advanced(settingFrom("hierarchy.maxImprovementIterations")),
  advanced(settingFrom("hierarchy.neighborhoodSize"))
];
const labelSettings = [
  settingFrom("labels.minLabelWords"), // TODO: combine into a single min-max control
  settingFrom("labels.maxLabelWords"),
  settingFrom("labels.preferredLabelLength"),
  settingFrom("labels.preferredLabelLengthDeviation"),
  settingFrom("labels.singleWordLabelWeight"),
  settingFrom("labels.allowNumbersInLabels"),
  settingFrom("labels.capitalizeNonFunctionWords"),
  settingFrom("labels.lowercaseFunctionWords"),
  advanced(settingFrom("labels.filtering.dashedWordsLabelFilter")),
  advanced(settingFrom("labels.filtering.dictionaryLabelFilter")),
  advanced(settingFrom("labels.filtering.minLengthLabelFilter")),
  advanced(settingFrom("labels.filtering.numberOnlyLabelFilter")),
  advanced(settingFrom("labels.filtering.trailingGenitiveLabelFilter")),
  advanced(settingFrom("labels.filtering.oneLetterWordLabelFilter")),
  advanced(settingFrom("labels.filtering.repeatedWordsLabelFilter")),
  advanced(settingFrom("labels.filtering.leftCompleteLabelFilter")),
  advanced(settingFrom("labels.filtering.rightCompleteLabelFilter")),
  advanced(settingFrom("labels.putPromotedLabelsAtHierarchyRoot")),
  advanced(settingFrom("labels.labelOverrideThreshold")),
  advanced(settingFrom("labels.queryWordLabelWeight")),
  advanced(settingFrom("labels.removeRepeatedSynonymsFromLabels")),
  advanced(settingFrom("labels.useBuiltInWordDatabaseForLabelFiltering"))
];
const mergingSettings = [
  settingFrom("merging.mergeThreshold"),
  settingFrom("merging.cloningControl"),
  settingFrom("merging.aggressiveCloningControl"),
  advanced(settingFrom("merging.flatMerging")),
  advanced(settingFrom("merging.hierarchicalMerging")),
  advanced(settingFrom("merging.hierarchicalMergingWithLabels")),
  advanced(settingFrom("merging.preciseHierarchicalMerging"))
];
const documentSettings = [
  settingFrom("documents.maxWordDf"),
  settingFrom("documents.wordDfThresholdScalingFactor"),
  settingFrom("documents.phraseDfThresholdScalingFactor"),
  settingFrom("documents.maxTokensPerDocument"),
  settingFrom("documents.accentFolding"),
  settingFrom("documents.boostFields"),
  settingFrom("documents.dashedWordsSynonymMarkerEnabled"),
  settingFrom("documents.dictionarySynonymMarkerEnabled"),
  settingFrom("documents.useHeuristicStemming"),
  settingFrom("documents.useBuiltInWordDatabaseForStemming")
];
const scoringSettings = [
  advanced(settingFrom("scoring.boostedFieldScorerWeight")),
  advanced(settingFrom("scoring.capitalizedWordLabelScorerWeight")),
  advanced(settingFrom("scoring.clusterSetDocumentOverlapLabelScorerWeight")),
  advanced(settingFrom("scoring.dictionaryWeightLabelScorerWeight")),
  advanced(settingFrom("scoring.documentCountLabelScorerWeight")),
  advanced(settingFrom("scoring.grammaticalVariantLabelScorerWeight")),
  advanced(settingFrom("scoring.queryWordLabelScorerWeight")),
  advanced(settingFrom("scoring.tfDfRatioLabelScorerWeight")),
  advanced(settingFrom("scoring.tfLabelScorerWeight")),
  advanced(settingFrom("scoring.unindexedWordLabelScorerWeight")),
  advanced(settingFrom("scoring.wordCountLabelScorerWeight"))
];

const dictionariesSettings = [
  createExcludedLabelsSetting("lingo3g", null, "dictionaries.labels")
];

const parameterStore = persistentStore(
  "parameters:algorithm:lingo3g",
  Object.assign(
    { language: "English" },
    collectDefaults(descriptorsById, [
      clusterSettings,
      hierarchySettings,
      labelSettings,
      documentSettings,
      mergingSettings,
      dictionariesSettings,
      scoringSettings
    ])
  )
);

// Add language setting separately, so that it doesn't participate in the process of
// collecting default values (language is not a parameter).
documentSettings.unshift(
  createLanguageSetting(
    "lingo3g",
    "Lingo3G",
    storeAccessors(parameterStore, "language"),
    // Hardcoded list of languages for now.
    [
      "Arabic",
      "Armenian",
      "Bulgarian",
      "Chinese-Simplified",
      "Chinese-Traditional",
      "Croatian",
      "Czech",
      "Danish",
      "Dutch",
      "English",
      "Estonian",
      "Finnish",
      "French",
      "Galician",
      "German",
      "Greek",
      "Hindi",
      "Hungarian",
      "Indonesian",
      "Irish",
      "Italian",
      "Japanese",
      "Korean",
      "Latvian",
      "Lithuanian",
      "Norwegian",
      "Polish",
      "Portuguese",
      "Romanian",
      "Russian",
      "Spanish",
      "Swedish",
      "Thai",
      "Turkish"
    ]
  )
);

const getter = setting => parameterStore[setting.id];
const settings = [
  {
    id: "lingo3g",
    type: "group",
    settings: [
      {
        id: "lingo3g:clusters",
        type: "group",
        label: "Clusters",
        settings: clusterSettings,
        description:
          "Parameters affecting the number, structure and content of clusters."
      },
      {
        id: "lingo3g:hierarchy",
        type: "group",
        label: "Hierarchy",
        settings: hierarchySettings,
        description: "Parameters affecting the cluster hierarchy."
      },
      {
        id: "lingo3g:labels",
        type: "group",
        label: "Labels",
        settings: labelSettings,
        description: "Customization of cluster labels."
      },
      {
        id: "lingo3g:dictionaries",
        type: "group",
        label: "Dictionaries",
        settings: dictionariesSettings,
        description: "Label exclusion dictionaries."
      },
      {
        id: "lingo3g:merging",
        type: "group",
        label: "Merging",
        settings: mergingSettings,
        description: "Cluster merging settings."
      },
      {
        id: "lingo3g:documents",
        type: "group",
        label: "Preprocessing",
        settings: documentSettings,
        description: "Document preprocessing settings."
      },
      {
        id: "lingo3g:scoring",
        type: "group",
        label: "Scoring",
        settings: scoringSettings,
        description: "Cluster scoring settings."
      }
    ],
    get: getter,
    set: (setting, val) => (parameterStore[setting.id] = val)
  }
];

export const lingo3g = {
  label: "Lingo3G",
  description:
    "Meaningful, well-described hierarchical clusters. Very fast, many tuning options.",
  descriptionHtml:
    "produces meaningful, well-described hierarchical clusters. Very fast, scalable, many tuning options (not exposed in this demo). Commercially available from <a href='https://carrotsearch.com' target='_blank'>Carrot Search</a>.",
  tag: "commercial",
  getSettings: () => settings,
  getLanguage: () => parameterStore.language,
  getDefaults: parameterStore.getDefaults,
  resetToDefaults: parameterStore.resetToDefaults,
  applyQueryHint: (params, query) => _set(params, "labels.queryHint", query)
};
